/**
 * «Аудит» — находки аудита склада (O12, D203) списками: единицы и
 * пересчёты, минусовые остатки, дубли номенклатуры. Только чтение: агент
 * ничего не меняет, правят в складской программе (D87). Сумм нет — деньги
 * склада считает финансовый агент.
 */

import { useQuery } from '@tanstack/react-query';
import type { ApiError } from '@shell/api';
import { Empty, ErrorState, Loading } from '@shell/states';
import { qty } from '../../api';
import { Notice, when } from '../../ui';
import { stockApi } from './api';
import type { Audit as Data } from './api';

export function Audit() {
  const query = useQuery<Data, ApiError>({
    queryKey: ['stock', 'audit'],
    queryFn: () => stockApi.audit(),
    refetchOnWindowFocus: true,
  });
  if (query.isPending) return <Loading text="Проверяю склад по последнему чтению…" />;
  if (query.error) return <ErrorState error={query.error} onRetry={() => query.refetch()} />;
  const audit = query.data;

  return (
    <div className="stock-stack">
      <p className="stock-hint">
        Только чтение: агент ничего не меняет, правят в складской программе.
        {audit.synced_at && <> Данные склада от {when(audit.synced_at)}.</>}
      </p>
      {audit.missing.map((item) => (
        <Notice key={item} kind="error">
          Канал ещё не привозил: {item}.
        </Notice>
      ))}
      {!audit.goods_read ? (
        <Empty
          title="Проверять нечего"
          text="Номенклатура склада ещё не приезжала. Она читается ночью; если её нет больше суток — проверьте службу в ресторане."
        />
      ) : audit.counts.total === 0 && audit.remains_read ? (
        <Empty title="Находок нет" text="Единицы, остатки и названия в порядке." />
      ) : (
        <>
          <div className="stock-tiles">
            <Tile label="Единицы и пересчёты" value={audit.counts.units} />
            <Tile
              label="Минусовые остатки"
              value={audit.remains_read ? audit.counts.negatives : null}
            />
            <Tile label="Дубли номенклатуры" value={audit.counts.duplicates} />
          </div>

          <section className="stock-card" aria-label="Единицы и пересчёты">
            <header className="stock-card__head">
              <h2 className="stock-card__title">1. Единицы и пересчёты</h2>
              <span className="faint">{audit.units.length}</span>
            </header>
            <p className="faint">«Привезли 8, а в черновике 10» — начинается отсюда.</p>
            {audit.units.length === 0 ? (
              <p className="faint">В порядке.</p>
            ) : (
              <ul className="stock-list">
                {audit.units.map((row) => (
                  <li key={row.good_id} className="stock-row">
                    <span className="stock-row__title">{row.name}</span>
                    <span className="stock-row__note">{row.problem}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="stock-card" aria-label="Минусовые остатки">
            <header className="stock-card__head">
              <h2 className="stock-card__title">2. Минусовые остатки</h2>
              <span className="faint">{audit.remains_read ? audit.negatives.length : '—'}</span>
            </header>
            {!audit.remains_read ? (
              <p className="faint">Остатков канал ещё не привозил.</p>
            ) : audit.negatives.length === 0 ? (
              <p className="faint">Нет.</p>
            ) : (
              <>
                <p className="faint">Минус значит: расход занесли, а приход — нет.</p>
                <ul className="stock-list">
                  {audit.negatives.map((row) => (
                    <li key={`${row.good_id}:${row.department ?? ''}`} className="stock-row stock-row--split">
                      <span className="stock-row__main">
                        <span className="stock-row__title">{row.name}</span>
                        {row.department && <span className="faint">{row.department}</span>}
                      </span>
                      <span className="num stock-negative">
                        {qty(row.quantity)} {row.unit ?? ''}
                      </span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </section>

          <section className="stock-card" aria-label="Дубли номенклатуры">
            <header className="stock-card__head">
              <h2 className="stock-card__title">3. Дубли номенклатуры</h2>
              <span className="faint">{audit.duplicates.length}</span>
            </header>
            {audit.duplicates.length === 0 ? (
              <p className="faint">Нет.</p>
            ) : (
              <>
                <p className="faint">Остатки и цена разъезжаются по двум карточкам одного товара.</p>
                <ul className="stock-list">
                  {audit.duplicates.map((row) => (
                    <li key={row.good_ids.join(':')} className="stock-row">
                      <span className="stock-row__title">
                        {row.names.map((name) => `«${name}»`).join(' = ')}
                      </span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </section>
        </>
      )}
    </div>
  );
}

function Tile({ label, value }: { label: string; value: number | null }) {
  return (
    <div className={`stock-tile${value ? ' stock-tile--warn' : ''}`}>
      <span className="stock-tile__label">{label}</span>
      <span className="stock-tile__value">{value === null ? '—' : value}</span>
    </div>
  );
}
